import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Form, FormField, FormItem, FormLabel, FormControl } from "@/components/ui/form";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { getAccountOwnershipInfo } from "./business-value-framework/accountService";
import { toast } from "sonner";

type Account = {
  company_id?: string;
  region: string;
  country: string;
  company_name: string;
  parent_company?: string;
  industry: string;
  account_manager: string;
};

type Deal = {
  id?: string;
  company_id?: string;
  user_id?: string;
  opportunity_name: string;
  value: number;
  solutions: string;
  customer_decision_maker: string;
  expected_closure: string;
  comments?: string;
};

const formatValue = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0
  }).format(value);
};

const KeyDealsTab = ({
  selectedAccount
}: {
  selectedAccount: Account | null;
}) => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [isSupervisor, setIsSupervisor] = useState(false);
  const [ownershipLoaded, setOwnershipLoaded] = useState(false);
  
  const form = useForm<Deal>({
    defaultValues: {
      opportunity_name: "",
      value: 0,
      solutions: "",
      customer_decision_maker: "",
      expected_closure: "",
      comments: ""
    }
  });
  
  // Get the current user's ID and account ownership info
  useEffect(() => {
    const getUserId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
      }
    };
    getUserId();
  }, []);

  useEffect(() => {
    const loadOwnershipInfo = async () => {
      if (selectedAccount?.company_id) {
        const ownershipInfo = await getAccountOwnershipInfo(selectedAccount.company_id);
        setCanEdit(ownershipInfo.canEdit);
        setIsSupervisor(ownershipInfo.canView && !ownershipInfo.isOwner);
        setOwnershipLoaded(true);
      }
    };

    loadOwnershipInfo();
  }, [selectedAccount]);
  
  const resetForm = () => {
    form.reset({
      opportunity_name: "",
      value: 0,
      solutions: "",
      customer_decision_maker: "",
      expected_closure: "",
      comments: ""
    });
  };
  
  const calculateEmptyRows = () => {
    const minVisibleRows = 10;
    const currentRows = deals.length;
    const emptyRowsNeeded = Math.max(minVisibleRows - currentRows, 0);
    return Array.from({
      length: emptyRowsNeeded
    }, (_, i) => i);
  };
  
  const emptyRows = calculateEmptyRows();
  
  useEffect(() => {
    if (selectedAccount?.company_id && userId) {
      fetchDeals();
    }
  }, [selectedAccount, userId]);
  
  const fetchDeals = async () => {
    if (!selectedAccount?.company_id || !userId) return;
    
    try {
      console.log('Fetching deals for company_id:', selectedAccount.company_id, 'user_id:', userId);
      
      // First, get the account owner for this company
      const { data: accountData, error: accountError } = await supabase
        .from('accounts')
        .select('user_id')
        .eq('company_id', selectedAccount.company_id)
        .single();
      
      if (accountError) {
        console.error('Error fetching account owner:', accountError);
        return;
      }
      
      const accountOwnerUserId = accountData.user_id;
      console.log('Account owner user_id:', accountOwnerUserId);
      
      // If current user is the account owner, fetch their deals
      if (userId === accountOwnerUserId) {
        const { data, error } = await supabase
          .from('deals_pipeline')
          .select('*')
          .eq('user_id', userId)
          .eq('company_id', selectedAccount.company_id);
        
        if (error) {
          console.error('Error fetching deals for account owner:', error);
          return;
        }
        
        console.log('Fetched deals for account owner:', data);
        setDeals(data || []);
      } else {
        // If current user is not the account owner, check if they're a supervisor
        const { data: subordinates, error: subordinatesError } = await supabase
          .rpc('get_user_subordinates', { supervisor_uuid: userId });
        
        if (subordinatesError) {
          console.error('Error fetching subordinates:', subordinatesError);
          return;
        }
        
        // Check if account owner is in the subordinates list
        const isSubordinate = subordinates && subordinates.some(
          (sub: any) => sub.subordinate_id === accountOwnerUserId
        );
        
        if (isSubordinate) {
          // User is a supervisor, fetch deals for the account owner
          const { data, error } = await supabase
            .from('deals_pipeline')
            .select('*')
            .eq('user_id', accountOwnerUserId)
            .eq('company_id', selectedAccount.company_id);
          
          if (error) {
            console.error('Error fetching deals for subordinate:', error);
            return;
          }
          
          console.log('Fetched deals for subordinate:', data);
          setDeals(data || []);
        } else {
          // User has no access to this account's deals
          console.log('User has no access to deals for this account');
          setDeals([]);
        }
      }
    } catch (error) {
      console.error('Error in fetchDeals:', error);
    }
  };
  
  const onSubmit = async (data: Deal) => {
    if (!selectedAccount?.company_id || !canEdit) {
      toast.error("Only account owners can create or edit deals");
      return;
    }
    
    try {
      if (editingDeal?.id) {
        const { error } = await supabase
          .from('deals_pipeline')
          .update({
            ...data,
            user_id: userId,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingDeal.id);
        
        if (error) {
          console.error('Error updating deal:', error);
          toast.error("Failed to update deal");
          return;
        }
        
        toast.success("Deal updated successfully");
      } else {
        const { error } = await supabase
          .from('deals_pipeline')
          .insert([{
            ...data,
            company_id: selectedAccount.company_id,
            user_id: userId
          }]);
        
        if (error) {
          console.error('Error creating deal:', error);
          toast.error("Failed to create deal");
          return;
        }
        
        toast.success("Deal created successfully");
      }
      
      setIsAddDialogOpen(false);
      resetForm();
      setEditingDeal(null);
      fetchDeals();
    } catch (error) {
      console.error('Error in onSubmit:', error);
      toast.error("An unexpected error occurred");
    }
  };
  
  const handleEdit = (deal: Deal) => {
    if (!canEdit) {
      toast.error("Only account owners can edit deals");
      return;
    }
    
    setEditingDeal(deal);
    form.reset({
      ...deal,
      expected_closure: deal.expected_closure ? new Date(deal.expected_closure).toISOString().split('T')[0] : ""
    });
    setIsAddDialogOpen(true);
  };
  
  const handleDelete = async (id: string) => {
    if (!canEdit) {
      toast.error("Only account owners can delete deals");
      return;
    }
    
    try {
      const { error } = await supabase
        .from('deals_pipeline')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Error deleting deal:', error);
        toast.error("Failed to delete deal");
        return;
      }
      
      toast.success("Deal deleted successfully");
      await fetchDeals();
    } catch (error) {
      console.error('Error in handleDelete:', error);
      toast.error("An unexpected error occurred");
    }
  };

  return (
    <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 py-0 my-[12px] h-[580px]">
      <CardContent className="mt-4 h-[550px] py-[12px] my-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            {selectedAccount && (
              <p className="text-[11px] text-primary-light">
                Selected Account ID: {selectedAccount.company_id} ({selectedAccount.company_name})
              </p>
            )}
          </div>
        </div>

        {ownershipLoaded && isSupervisor && (
          <div className="mb-2 p-1 bg-primary/20 text-primary border-primary/30 rounded-lg">
            <p className="text-xs text-secondary-800">
              You are viewing this account as a supervisor. Only the account owner can add, edit, or delete deals.
            </p>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white">Deals Pipeline</h2>
            <Dialog open={isAddDialogOpen} onOpenChange={open => {
              if (!canEdit && open) {
                toast.error("Only account owners can add deals");
                return;
              }
              setIsAddDialogOpen(open);
              if (!open) {
                resetForm();
                setEditingDeal(null);
              }
            }}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline" 
                  className="border-primary/20 bg-primary hover:bg-primary/90 text-black"
                  disabled={!canEdit}
                  onClick={() => {
                    if (!canEdit) {
                      toast.error("Only account owners can add deals");
                      return;
                    }
                    setEditingDeal(null);
                    resetForm();
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Opportunity
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white text-black">
                <DialogHeader>
                  <DialogTitle>{editingDeal ? 'Edit Deal' : 'Add New Deal'}</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField control={form.control} name="opportunity_name" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Opportunity Name</FormLabel>
                        <FormControl>
                          <Input className="bg-white text-black" {...field} />
                        </FormControl>
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="value" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Value</FormLabel>
                        <FormControl>
                          <Input 
                            type="text" 
                            className="bg-white text-black"
                            value={field.value ? formatValue(field.value) : ''} 
                            onChange={e => {
                              const rawValue = e.target.value.replace(/,/g, '');
                              const numValue = parseInt(rawValue) || 0;
                              field.onChange(numValue);
                            }} 
                            onBlur={e => {
                              const rawValue = e.target.value.replace(/,/g, '');
                              const numValue = parseInt(rawValue) || 0;
                              e.target.value = formatValue(numValue);
                            }} 
                          />
                        </FormControl>
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="solutions" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Solution(s)</FormLabel>
                        <FormControl>
                          <Input className="bg-white text-black" {...field} />
                        </FormControl>
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="customer_decision_maker" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Customer Decision Maker</FormLabel>
                        <FormControl>
                          <Input className="bg-white text-black" {...field} />
                        </FormControl>
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="expected_closure" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Expected Closure</FormLabel>
                        <FormControl>
                          <Input className="bg-white text-black" type="date" {...field} />
                        </FormControl>
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="comments" render={({ field }) => (
                      <FormItem>
                        <FormLabel>Comments</FormLabel>
                        <FormControl>
                          <Input className="bg-white text-black" {...field} />
                        </FormControl>
                      </FormItem>
                    )} />
                    <Button type="submit" className="w-full">
                      {editingDeal ? 'Update Deal' : 'Add New Opportunity'}
                    </Button>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </div>
          <div className="h-[420px]">
            <ScrollArea className="h-[420px] border border-primary/20 rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary/30 text-[11px] hover:bg-primary/30">
                    <TableHead className="text-primary-light text-[11px] text-left w-[40px]">SN</TableHead>
                    <TableHead className="text-primary-light text-[11px] text-left w-[150px]">Opportunity Name</TableHead>
                    <TableHead className="text-primary-light text-[11px] text-left w-[100px]">Value ($)</TableHead>
                    <TableHead className="text-primary-light text-[11px] text-left w-[120px]">Solution(s)</TableHead>
                    <TableHead className="text-primary-light text-[11px] text-left w-[140px]">Customer Decision Maker</TableHead>
                    <TableHead className="text-primary-light text-[11px] text-left w-[120px]">Expected Closure</TableHead>
                    <TableHead className="text-primary-light text-[11px] text-left w-[120px]">Comments</TableHead>
                    <TableHead className="text-primary-light text-[11px] text-left w-[80px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deals.map((deal, index) => (
                    <TableRow key={deal.id} className="bg-secondary-light/5 text-[11px]">
                      <TableCell className="text-white text-[11px] text-left w-[40px]">{index + 1}</TableCell>
                      <TableCell className="text-white text-[11px] text-left w-[150px]">{deal.opportunity_name}</TableCell>
                      <TableCell className="text-white text-[11px] text-left w-[100px]">{formatValue(deal.value)}</TableCell>
                      <TableCell className="text-white text-[11px] text-left w-[120px]">{deal.solutions}</TableCell>
                      <TableCell className="text-white text-[11px] text-left w-[140px]">{deal.customer_decision_maker}</TableCell>
                      <TableCell className="text-white text-[11px] text-left w-[120px]">
                        {deal.expected_closure ? new Date(deal.expected_closure).toLocaleDateString() : ''}
                      </TableCell>
                      <TableCell className="text-white text-left w-[120px]">{deal.comments}</TableCell>
                      <TableCell className="text-white text-left w-[80px]">
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleEdit(deal)} 
                            className="h-8 w-8 text-primary hover:text-white hover:bg-primary"
                            disabled={!canEdit}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 text-primary hover:text-white hover:bg-primary"
                                disabled={!canEdit}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete the opportunity "{deal.opportunity_name}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deal.id && handleDelete(deal.id)}>
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {emptyRows.map(index => (
                    <TableRow key={`empty-${index}`} className="bg-secondary-light/5">
                      <TableCell className="text-white text-[11px] text-left w-[40px]">&nbsp;</TableCell>
                      <TableCell className="text-white text-[11px] text-left w-[150px]">&nbsp;</TableCell>
                      <TableCell className="text-white text-[11px] text-left w-[100px]">&nbsp;</TableCell>
                      <TableCell className="text-white text-[11px] text-left w-[120px]">&nbsp;</TableCell>
                      <TableCell className="text-white text-[11px] text-left w-[140px]">&nbsp;</TableCell>
                      <TableCell className="text-white text-[11px] text-left w-[120px]">&nbsp;</TableCell>
                      <TableCell className="text-white text-[11px] text-left w-[120px]">&nbsp;</TableCell>
                      <TableCell className="text-white text-[11px] text-left w-[80px]">&nbsp;</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default KeyDealsTab;
